<template>
  <v-dialog v-model="modalData.dialog" max-width="800px" persistent scrollable>
    <v-card>
      <v-card-title class="d-flex align-center">
        <span class="headline">{{ formTitle }}</span>
        <v-spacer></v-spacer>
        <v-btn icon color="#26223c" @click="handleCloseModalForm">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-overlay :value="loader">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>

      <v-card-text>
        <v-container>
          <v-form ref="form" v-model="formValid" lazy-validation>
            <v-row>
              <v-col cols="12" md="8">
                <v-combobox
                  v-model="selectedItem"
                  :items="activeItemCollection"
                  :rules="requiredRules"
                  label="Item"
                  item-text="ReferenceName"
                  return-object
                  :error-messages="messages['item_id']"
                  @input="onItemSelected"
                >
                  <template v-slot:no-data>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>No item matching!</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-combobox>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="formModel.min_quantity"
                  type="number"
                  min="0"
                  :rules="minQtyRules"
                  label="Alert if total stock <= (X)"
                  :error-messages="messages['min_quantity']"
                  @input="messages['min_quantity'] = ''"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                    v-model="formModel.repeat_every_days"
                    :items="repeatOptions"
                    item-text="label"
                    item-value="value"
                    label="Repeat"
                ></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                    v-model="formModel.user_groups_id"
                    :items="userGroupCollection"
                    item-text="groupName"
                    item-value="id"
                    label="Contact emails"
                    :id="dynamicID"
                ></v-select>
              </v-col>

              <v-col cols="12" md="4" class="d-flex align-center">
                <v-switch v-model="formModel.is_active" label="Active" inset />
              </v-col>

              <v-col cols="12" md="8">
                <v-textarea
                  v-model="formModel.note"
                  label="Note (optional)"
                  auto-grow
                  rows="2"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="handleCloseModalForm">Cancel</v-btn>
        <v-btn color="primary" dark :loading="loader" @click="handleSaveModalForm">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ApiService from "@/core/services/api.service";

export const ENDPOINT = "stock/reminder/";

const initialFormData = () => ({
  id: null,
  item_id: null,
  min_quantity: 0,
  is_active: true,
  note: "",
  repeat_every_days: 0,
  user_groups_id: null,
});

export default {
  name: "StockReminderForm",
  props: ["modalData"],

  data() {
    return {
      loader: false,
      formValid: false,
      formModel: Object.assign({}, initialFormData()),
      selectedItem: null,
      selectedItemID: this.modalData?.item_id,
      messages: {},
      requiredRules: [(v) => !!v || "This field is required"],
      minQtyRules: [
        (v) => v !== null && v !== "" || "This field is required",
        (v) => Number(v) >= 0 || "Must be >= 0",
      ],
      repeatOptions:[
        {value: 0, label: '0 - Once'},
        {value: 1, label: '1 - Daily'},
        {value: 2, label: '2 - Every 2 days'},
        {value: 3, label: '3 - Every 3 days'},
      ],
    };
  },

  computed: {
    ...mapGetters(["userGroupCollection","itemCollection"]),
    activeItemCollection() {
        return (this.itemCollection || []).filter((item => item.archived_at === null));
    },
    dynamicID() {
      let text = "dynamicID";
      let chars = "abcdefghijklmnopqrstuvwxyz";

      for (let i = 0; i < 10; i++) {
        text += chars.charAt(Math.floor(Math.random() * chars.length));
      }

      return text;
    },
    formTitle() {
      return this.formModel.id ? "Edit stock reminder" : "New stock reminder";
    },
  },

  watch: {
    "modalData.dialog": {
      handler(val) {
        if (!val) return;

        if (!this.modalData.editedId) {
          this.resetForm();
        }
        this.resetErrors();
      },
    },

    "modalData.editedId": {
      immediate: true,
      handler(id) {
        if (!id) return;
        this.fetchById(id);
      },
    },
    activeItemCollection() {
      this.syncSelectedItem();
    },
    "formModel.item_id"() {
      this.syncSelectedItem();
    },
  },

  methods: {
    ...mapActions(["fetchUserGroups","fetchItem"]),

    resetForm() {
      this.formModel = Object.assign({}, initialFormData());
      this.selectedItem = null;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },

    resetErrors() {
      this.messages = {
        item_id: "",
        min_quantity: "",
      };
    },

    handleCloseModalForm() {
      this.$emit("closeModalForm");
    },

    onItemSelected(val) {
      this.messages["item_id"] = "";
      if (val && typeof val === "object") {
        this.formModel.item_id = val.id;
      } else {
        this.formModel.item_id = null;
      }
    },

    async fetchById(id) {
      this.loader = true;
      try {
        const res = await ApiService.get(ENDPOINT + id);
      
        const payload = res.data?.data ?? res.data;
        this.formModel = { ...initialFormData(), ...payload };
      
        await this.ensureItemsLoaded();
        this.syncSelectedItem();
      } catch (e) {
        console.log("Reminder load error:", e);
      } finally {
        this.loader = false;
      }
    },

    async ensureItemsLoaded() {
      if (!this.itemCollection || this.itemCollection.length === 0) {
        await this.fetchItem();
      }
    },

    syncSelectedItem() {
      const id = this.formModel?.item_id;
      if (!id) {
        this.selectedItem = null;
        return;
      }

      this.selectedItem =
        this.activeItemCollection.find(i => i.id === id) || null;
    },

    async handleSaveModalForm() {
      this.$refs.form.validate();
      if (!this.formValid) return;

      this.loader = true;
      this.resetErrors();

      try {
        const payload = Object.assign({}, this.formModel);

        if (payload.id) {
          await ApiService.put(ENDPOINT + payload.id, payload);
        } else {
          await ApiService.post(ENDPOINT, payload);
        }

        this.$emit("saveModalForm");
      } catch (error) {
        if (error?.response?.data) {
          const errors = error.response.data;
          Object.keys(errors).forEach((field) => {
            this.messages[field] = Array.isArray(errors[field])
              ? errors[field][0]
              : errors[field];
          });
        } else {
          console.log("Reminder save error:", error);
        }
      } finally {
        this.loader = false;
      }
    },
  },

  mounted() {
    this.fetchUserGroups();
    this.fetchItem();
  },
};
</script>
